import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Actions,
  Department,
  Workflow,
  WorkflowCategory,
  WorkflowType
} from 'src/app/models';
import {
  ClientService,
  SecurityService,
  SystemService,
  Utilities,
  WorkflowContextService,
  WorkflowService
} from 'src/app/services';
import { WorkflowJanusService } from '../workflow-janus.service';
import { WorkflowVersion } from 'src/app/models/workflow-version';
import { WorkflowGraph } from 'src/app/models/workflow-graph';
import {
  Activity,
  ActivityModel,
  OnStartedActivity,
  StartWorkflowActivity,
  StartWorkflowActivityModel
} from 'src/app/models/activities';
import { ItemTag } from 'src/app/models/item-tag';
import { WorkflowTag } from 'src/app/models/workflow-tag';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { range } from 'lodash';
import moment from 'moment';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConditionTarget } from 'src/app/components/system/condition-builder/condition-builder.model';
import { ToastrService } from 'ngx-toastr';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ModalConfirmComponent } from 'src/app/components/system/modal-confirm/modal-confirm.component';
import { DataEntity } from 'src/app/models/data-entities';
import { Observable } from 'rxjs';
import { Requirement } from 'src/app/models/requirement';
import { AdvancedPermissionsEditorComponent } from 'src/app/components/security/advanced-permissions-editor/advanced-permissions-editor.component';

@Component({
  selector: 'app-blank-workflow',
  templateUrl: './blank-workflow.component.html',
  styleUrls: ['./blank-workflow.component.scss'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class BlankWorkflowComponent implements OnInit {
  @ViewChild('advancedPermissions', { static: false })
  advancedPerm: AdvancedPermissionsEditorComponent;
  EMPTY_GUID: string = Utilities.EMPTY_GUID;
  isSaving = false;
  // Form
  form!: FormGroup;

  // UI State
  isEditorVisible: boolean = false;

  // Status and Department Options
  statusOptions = [{ label: 'Active', value: 'active' }];
  departmentOptions: { label: string; value: string }[] = [];

  // Advanced Number Prefix
  isAdvancedNumberPrefixEditorEnabled: boolean = false;
  isPrefixEditMode: boolean = false;

  // Workflow Data
  workflows$ = this.workflowJanusService.workflows$;
  workflow: Workflow;
  workflowId: string;
  clientMode = true;
  workflowCategories: WorkflowCategory[] = [];
  categoriesEnabled = false;

  // Selected Values
  selectedDepartmentId: string = '';

  // Category Management
  isCategoryEnabled: boolean = false;
  showAddCategoryForm: boolean = false;
  @ViewChild('disableCategoriesModal', { static: false })
  disableCategoriesModal: ModalConfirmComponent;
  loading = false;

  categoryDeleteModal: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to delete this category?',
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL'
  };
  categoryToDelete: WorkflowCategory | null = null;
  newCategory = {
    name: ''
  };
  categories: WorkflowCategory[] = [];

  // Added by Amisha on 2026-06-03:
  // Keeps the Janus workflow settings editor self-contained by showing highlighted
  // application-description tokens and a searchable data-entity picker without
  // depending on the non-Janus autocomplete component.
  entityOptions: { label: string; value: string }[] = [];
  filteredEntityOptions: { label: string; value: string }[] = [];
  @ViewChild('descInput', { read: ElementRef }) descInput!: ElementRef;
  selectedEntity: string = '';
  applicationDescription: string = '';
  entitySearchText: string = '';
  showEntitySearchResults: boolean = false;
  cursorPosition: number = 0;
  @ViewChild('descriptionPreview', { static: false })
  descriptionPreview?: ElementRef<HTMLDivElement>;
  @ViewChild('entitySearchInput', { static: false })
  entitySearchInput?: ElementRef<HTMLInputElement>;

  // Tags
  tagOptions: { label: string; value: string }[] = [];
  selectedTagId: string = '';
  selectedTags: any[] = [];
  allTags: any[] = [];
  workflowTags: ItemTag[] = [];
  tagToDelete: any = null;

  // Delete Confirmation Modal
  deleteConfirmModal: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to delete this note?',
    yesNoBtn: true,
    replaceYesWith: 'SAVE',
    replaceNoWith: 'CANCEL'
  };

  // Application Number Prefix
  numberPrefix: string = '';
  fixedNumberPreview: string = '';
  isAutoResetEnabled: boolean = false;
  nextResetDate: string;
  calcNextDate = false;
  months: { label: string; value: number }[] = [];
  days: { label: string; value: number }[] = [];

  // Visible to Public
  visibleToPublic: boolean = false;

  // Auto Void
  isAutoVoidEnabled: boolean = false;
  activityOptions: { label: string; value: string }[] = [];
  autoVoidExample: string | null = null;
  autoVoidActivities: Activity<ActivityModel>[];

  // Administrative Role
  adminRoles: any[] = [];
  adminRoleId: string = '';
  canManageRoles: boolean = false;
  adminRoleValid: boolean = true;
  roleOptions: { label: string; value: string }[] = [];

  // Workflow Type
  typeOptions = [
    { label: 'Permit', value: 0 },
    { label: 'Renewable', value: 4 },
    { label: 'Other', value: 3 },
    { label: 'Mobile Inspection', value: 5 }
  ];
  renewalWorkflowOptions: any[] = [];
  expirationOptions = [
    { label: 'Does not expire (infinite)', value: 'infinite' },
    { label: 'Expires at end of effective year', value: 'end-of-issue-year' },
    {
      label: 'Set a term length in months from effective date',
      value: 'custom-months'
    },
    {
      label: 'Calculate expiration date with custom formula',
      value: 'custom-formula'
    }
  ];
  selectedExpirationOption: {
    label: string;
    value: string;
    expSettings?: ConditionTarget[];
  };
  customFormulaForm = new FormGroup({
    formula: new FormControl(null)
  });
  expirationCustomForumulaExcludedTemplateCodes = ['EXPIRATION_DATE'];
  customFormulaCriteria: ConditionTarget[] = [];

  // Requirements
  requirements: any[] = [];
  newRequirement: Requirement;
  entities: DataEntity[] = [];

  // Departments
  departments: Department[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _clientService: ClientService,
    private _context: WorkflowContextService,
    private _workflowService: WorkflowService,
    private workflowJanusService: WorkflowJanusService,
    private _systemService: SystemService,
    private _securityService: SecurityService,
    private _toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) {}

  // Category Management Methods
  openPrefixEditor() {
    this.isPrefixEditMode = true;
  }

  closePrefixEditor() {
    this.isPrefixEditMode = false;
  }

  onCategoriesToggle(enabled: boolean) {
    if (!enabled) {
      setTimeout(() => {
        this.isCategoryEnabled = true;
      }, 0);
      this.disableCategoriesModal.open();
    } else {
      this.isCategoryEnabled = true;
      if (enabled) {
        this.ensureStaticCategories();
        this.categories.sort((a, b) => (a?.ordinal || 0) - (b?.ordinal || 0));
      }
    }
  }

  confirmDisableCategories() {
    if (this.categories.length > 0) {
      this.loading = true;
      const deletePromises = this.categories.map(cat =>
        this._workflowService.deleteWorkflowCategory(cat.id).toPromise()
      );
      Promise.all(deletePromises)
        .then(() => {
          this.categories = [];
          this.isCategoryEnabled = false;
          this._toastr.success(
            'Categories have been disabled and custom categories removed.'
          );
          this.loading = false;
          this.disableCategoriesModal.cancel();
        })
        .catch(() => {
          this._toastr.error('Failed to remove some categories.');
          this.loading = false;
          this.disableCategoriesModal.cancel();
        });
    } else {
      this.isCategoryEnabled = false;
      this.disableCategoriesModal.cancel();
    }
  }

  cancelDisableCategories() {
    this.isCategoryEnabled = true;
  }

  onDrop(event: CdkDragDrop<WorkflowCategory[]>) {
    if (
      event.previousIndex !== event.currentIndex &&
      this.categories?.length > 0
    ) {
      this.categories = this.categories.filter(c => c != null);
      moveItemInArray(this.categories, event.previousIndex, event.currentIndex);
      this.categories.forEach((cat, index) => {
        cat.ordinal = index + 1;
      });
      this.saveCategoriesOrder();
    }
  }

  private saveCategoriesOrder() {
    if (!this.categories?.length) return;
    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;
    if (isNewWorkflow) {
      this._toastr.success('Order updated. Will be saved with workflow.');
      return;
    }
    this.loading = true;
    const savePromises = this.categories.map(cat =>
      this._workflowService.saveWorkflowCategory(cat).toPromise()
    );
    Promise.all(savePromises)
      .then(() => {
        this._toastr.success('Category order updated successfully.');
        this.loading = false;
      })
      .catch(() => {
        this._toastr.error('Failed to update order.');
        this.loading = false;
      });
  }

  // private ensureStaticCategories() {
  //   const hasAttachments = this.categories.some(
  //     c => c.staticCategoryType === 'attachments'
  //   );
  //   const hasNotes = this.categories.some(
  //     c => c.staticCategoryType === 'notes'
  //   );
  //   const hasSubmit = this.categories.some(
  //     c => c.staticCategoryType === 'submit'
  //   );
  //   if (!hasAttachments) {
  //     this.categories.push(new WorkflowCategory({
  //       id: Utilities.generateId(),
  //       name: 'Attachments',
  //       description: 'Document attachments and files',
  //       workflowId: this.workflowId,
  //       ordinal: this.categories.length + 1,
  //       isStatic: true,
  //       staticCategoryType: 'attachments'
  //     }));
  //   }
  //   if (!hasNotes) {
  //     this.categories.push(new WorkflowCategory({
  //       id: Utilities.generateId(),
  //       name: 'Notes',
  //       description: 'Internal notes and comments',
  //       workflowId: this.workflowId,
  //       ordinal: this.categories.length + 1,
  //       isStatic: true,
  //       staticCategoryType: 'notes'
  //     }));
  //   }
  //   // Add Submit category if it doesn't exist
  //   if (!hasSubmit) {
  //     this.categories.push(new WorkflowCategory({
  //       id: Utilities.generateId(),
  //       name: 'Submit',
  //       description: 'Submit application',
  //       workflowId: this.workflowId,
  //       ordinal: this.categories.length + 1,
  //       isStatic: true,
  //       staticCategoryType: 'submit'
  //     }));
  //   }
  // }

  private ensureStaticCategories() {
    const hasAttachments = this.categories.some(
      c => c.staticCategoryType === 'attachments'
    );

    const hasNotes = this.categories.some(
      c => c.staticCategoryType === 'notes'
    );

    const categoriesToAdd: WorkflowCategory[] = [];

    if (!hasAttachments) {
      const attachmentsCategory = new WorkflowCategory({
        id: Utilities.generateId(),
        name: 'Attachments',
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1,
        isStatic: true,
        staticCategoryType: 'attachments'
      });

      this.categories.push(attachmentsCategory);
      categoriesToAdd.push(attachmentsCategory);
    }

    if (!hasNotes) {
      const notesCategory = new WorkflowCategory({
        id: Utilities.generateId(),
        name: 'Notes',
        workflowId: this.workflowId,
        ordinal: this.categories.length + 1,
        isStatic: true,
        staticCategoryType: 'notes'
      });

      this.categories.push(notesCategory);
      categoriesToAdd.push(notesCategory);
    }

    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;

    if (categoriesToAdd.length > 0 && !isNewWorkflow) {
      this.loading = true;

      const savePromises = categoriesToAdd.map(category =>
        this._workflowService.saveWorkflowCategory(category).toPromise()
      );

      Promise.all(savePromises)
        .then(savedCategories => {
          savedCategories.forEach((savedCategory, index) => {
            const categoryIndex = this.categories.findIndex(
              c => c.id === categoriesToAdd[index].id
            );

            if (categoryIndex > -1) {
              this.categories[categoryIndex] = savedCategory;
            }
          });

          this.loading = false;
        })
        .catch(() => {
          this._toastr.error('Failed to save system categories.');
          this.loading = false;
        });
    }
  }

  openCategoryForm() {
    this.showAddCategoryForm = true;
  }

  closeCategoryForm() {
    this.showAddCategoryForm = false;
    this.newCategory = { name: '' };
  }

  addCategory() {
    if (!this.newCategory.name?.trim()) {
      this._toastr.error('Category name is required');
      return;
    }
    const newCat = new WorkflowCategory({
      id: Utilities.generateId(),
      name: this.newCategory.name.trim(),
      workflowId: this.workflowId,
      ordinal: this.categories.length + 1
    });
    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;
    if (isNewWorkflow) {
      this.categories.push(newCat);
      this._toastr.success(
        `Category "${newCat.name}" will be saved with the workflow.`
      );
    } else {
      this.loading = true;
      this._workflowService.saveWorkflowCategory(newCat).subscribe({
        next: saved => {
          this.categories.push(saved);
          this._toastr.success(
            `Category "${saved.name}" has been created successfully.`
          );
          this.loading = false;
        },
        error: () => {
          this._toastr.error('Failed to create category.');
          this.loading = false;
        }
      });
    }
    this.closeCategoryForm();
  }

  deleteCategory(cat: WorkflowCategory) {
    this.categoryToDelete = cat;
    this.categoryDeleteModal = {
      ...this.categoryDeleteModal,
      open: true,
      modalDescp: `Are you sure you want to delete (${cat.name}) category?`
    };
  }

  confirmDeleteCategory() {
    if (!this.categoryToDelete) return;
    const cat = this.categoryToDelete;
    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;
    if (isNewWorkflow) {
      this.categories = this.categories.filter(c => c.id !== cat.id);
      this._toastr.success(`Category "${cat.name}" removed.`);
    } else {
      this.loading = true;
      this._workflowService.deleteWorkflowCategory(cat.id).subscribe({
        next: () => {
          this.categories = this.categories.filter(c => c.id !== cat.id);
          this._toastr.success(
            `Category "${cat.name}" has been deleted successfully.`
          );
          this.loading = false;
        },
        error: () => {
          this._toastr.error('Failed to delete category.');
          this.loading = false;
        }
      });
    }
    this.closeCategoryDeleteModal();
  }

  closeCategoryDeleteModal() {
    this.categoryDeleteModal = {
      ...this.categoryDeleteModal,
      open: false
    };
    this.categoryToDelete = null;
  }

  editCategory(cat: WorkflowCategory) {
    if (!cat) {
      this._toastr.error('Invalid category selected.');
      return;
    }
    this.categories.forEach(c => (c.isEditing = false));
    (cat as any)._backup = {
      name: cat.name
    };
    cat.isEditing = true;
  }

  saveCategory(cat: WorkflowCategory) {
    if (!cat || !cat.name?.trim()) {
      this._toastr.error('Invalid category data.');
      return;
    }
    const isNewWorkflow =
      !this.workflowId || this.workflowId === Utilities.EMPTY_GUID;
    if (isNewWorkflow) {
      cat.isEditing = false;
      this._toastr.success(
        `Category "${cat.name}" will be saved with the workflow.`
      );
    } else {
      this.loading = true;
      this._workflowService.saveWorkflowCategory(cat).subscribe({
        next: updated => {
          const index = this.categories.findIndex(c => c.id === updated.id);
          if (index > -1) {
            this.categories[index] = {
              ...updated,
              isEditing: false
            };
          }
          this._toastr.success(
            `Category "${updated.name}" updated successfully.`
          );
          this.loading = false;
        },
        error: () => {
          this._toastr.error('Failed to update category.');
          this.loading = false;
        }
      });
    }
  }

  cancelEdit(cat: WorkflowCategory) {
    const backup = (cat as any)._backup;
    if (backup) {
      cat.name = backup.name;
    }
    cat.isEditing = false;
  }

  get applicationDescriptionPreviewHtml(): string {
    return this.formatApplicationDescriptionTemplate(
      this.workflow?.version?.applicationDescriptionTemplate || ''
    );
  }

  onDescriptionInput(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.workflow.version.applicationDescriptionTemplate = textarea.value;
    this.cursorPosition = textarea.selectionStart ?? textarea.value.length;
    this.syncDescriptionPreviewScroll(textarea.scrollTop);
  }

  onCursorChange(event: any) {
    this.cursorPosition = event.target.selectionStart;
  }

  onDescriptionScroll(event: Event) {
    const textarea = event.target as HTMLTextAreaElement;
    this.syncDescriptionPreviewScroll(textarea.scrollTop);
  }

  onEntitySearchFocus() {
    this.openEntitySearchDropdown();
  }

  openEntitySearchDropdown() {
    this.filterEntityOptions();
    this.showEntitySearchResults = true;
  }

  onEntitySearchInput() {
    this.openEntitySearchDropdown();
  }

  onEntitySearchBlur() {
    setTimeout(() => {
      this.showEntitySearchResults = false;
    }, 150);
  }

  toggleEntitySearchDropdown() {
    if (this.showEntitySearchResults) {
      this.showEntitySearchResults = false;
      return;
    }

    this.openEntitySearchDropdown();

    setTimeout(() => {
      this.entitySearchInput?.nativeElement.focus();
    });
  }

  onEntitySelect(option: any) {
    if (!option) {
      return;
    }

    const currentText =
      this.workflow.version.applicationDescriptionTemplate || '';
    const selectedValue = option?.value || option;
    const insertText = `\${${selectedValue}}`;
    const insertAt =
      this.cursorPosition > 0 ? this.cursorPosition : currentText.length;
    const before = currentText.substring(0, insertAt);
    const after = currentText.substring(insertAt);

    this.workflow.version.applicationDescriptionTemplate =
      before + insertText + after;

    this.cursorPosition = insertAt + insertText.length;
    this.entitySearchText = '';
    this.filterEntityOptions();
    this.showEntitySearchResults = false;

    setTimeout(() => {
      const target = this.descInput?.nativeElement as HTMLTextAreaElement;
      if (!target) {
        return;
      }

      target.focus();
      target.setSelectionRange(this.cursorPosition, this.cursorPosition);
      this.syncDescriptionPreviewScroll(target.scrollTop);
    });
  }

  private filterEntityOptions() {
    const term = (this.entitySearchText || '').trim().toLowerCase();
    const options = this.entityOptions || [];

    if (!term) {
      this.filteredEntityOptions = options.slice(0, 10);
      return;
    }

    const startsWith = options.filter(option =>
      option.label.toLowerCase().startsWith(term)
    );

    const contains = options.filter(
      option =>
        !option.label.toLowerCase().startsWith(term) &&
        option.label.toLowerCase().includes(term)
    );

    this.filteredEntityOptions = [...startsWith, ...contains].slice(0, 10);
  }

  private syncDescriptionPreviewScroll(scrollTop: number) {
    if (this.descriptionPreview?.nativeElement) {
      this.descriptionPreview.nativeElement.scrollTop = scrollTop;
    }
  }

  private formatApplicationDescriptionTemplate(template: string): string {
    if (!template) {
      return '';
    }

    const tokenPattern = /\$\{([^{}]+)\}/g;
    let lastIndex = 0;
    let result = '';
    let match: RegExpExecArray | null;

    while ((match = tokenPattern.exec(template)) !== null) {
      result += this.escapeHtml(template.slice(lastIndex, match.index));
      result += `<span class="description-token">${this.escapeHtml(
        match[1]
      )}</span>`;
      lastIndex = match.index + match[0].length;
    }

    result += this.escapeHtml(template.slice(lastIndex));
    return result;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Tags Methods
  onTagSelect(event: any) {
    this.selectedTagId = event?.value || event;
  }

  addTag() {
    if (!this.selectedTagId) return;
    const tag = this.allTags.find(t => t.id === this.selectedTagId);
    if (!tag) return;
    // ❌ duplicate check
    const alreadyExists = this.selectedTags.some(t => t.id === tag.id);
    if (alreadyExists) return;
    const addTag = () => {
      this.workflowTags.push(<ItemTag>{
        itemId: this.workflowId,
        tagId: tag.id,
        tag
      });
      this.workflow.workflowTags.push(<WorkflowTag>{
        workflowId: this.workflowId,
        tagId: tag.id,
        tag
      });
    };
    if (this.workflowId) {
      this._systemService
        .addWorkflowTag(<WorkflowTag>{
          workflowId: this.workflowId,
          tagId: tag.id
        })
        .subscribe(result => {
          addTag();
        });
    } else {
      addTag();
    }
    this.selectedTags.push(tag);
  }

  removeTag(tag: any) {
    this.tagToDelete = tag;
    this.setDeleteConfirmModal({
      open: true,
      // title: 'Delete Tag',
      modalDescp: `Are you sure you want to remove (${tag.name}) from this workflow?`,
      // modalSubDescp: `
      //   <p>Some things to take note of.</p>
      //   <ul>
      //     <li>This tag will be removed from this workflow.</li>
      //   </ul>
      // `,
      replaceYesWith: 'DELETE',
      replaceNoWith: 'CANCEL'
    });
  }

  confirmDeleteNote() {
    if (!this.tagToDelete) return;
    const tag = this.tagToDelete;
    // 🔥 local remove
    this.selectedTags = this.selectedTags.filter(t => t.id !== tag.id);
    const removeTag = () => {
      const idx = this.workflowTags.findIndex(wt => wt.tagId === tag.id);
      if (idx !== -1) this.workflowTags.splice(idx, 1);
    };
    if (this.workflowId) {
      this._systemService
        .removeWorkflowTag({
          workflowId: this.workflowId,
          tagId: tag.id
        } as WorkflowTag)
        .subscribe(() => {
          removeTag();
        });
    } else {
      removeTag();
    }
    // modal close
    this.setDeleteConfirmModal({ open: false });
    this.tagToDelete = null;
  }

  closeDeleteNoteModal() {
    this.setDeleteConfirmModal({ open: false });
    this.tagToDelete = null;
  }

  private setDeleteConfirmModal(
    updates: Partial<CommonConfirmationModalData>
  ): void {
    this.deleteConfirmModal = {
      ...this.deleteConfirmModal,
      ...updates
    };
  }

  // Application Number Prefix Methods
  updateCriteria(criteria: ConditionTarget[]) {
    this.workflow.numberPrefixCriteria = criteria;
    let hasValue = false;
    if (criteria?.length) {
      criteria.forEach(c => {
        if ((c.value || '') !== '') hasValue = true;
      });
    }
    this.numberPrefix = hasValue ? 'done' : '';
    // 🔥 IMPORTANT
    this.form.get('numberPrefix')?.setValue(this.numberPrefix);
  }

  getThenLabel = (clause: any): string => {
    return clause?.value || '';
  };

  calculateFixedNumberPreview() {
    let num = null;
    let len = null;
    if (this.form.controls['startingNumber']) {
      num = this.form.controls['startingNumber'].value;
    }

    if (this.form.controls['totalLength']) {
      len = this.form.controls['totalLength'].value;
    }
    if (num && len) {
      this.fixedNumberPreview = num.toString().padStart(len, '0');
    } else if (num) {
      this.fixedNumberPreview = num.toString();
    } else {
      this.fixedNumberPreview = '';
    }
  }
  onMonthChange(value: any) {
    this.form.patchValue({
      resetMonth: value
    });

    this.calculateNextResetDate();
  }

  onDayChange(value: any) {
    this.form.patchValue({
      resetDay: value
    });

    this.calculateNextResetDate();
  }

  calculateNextResetDate() {
    const month = this.form.value.resetMonth;
    const day = this.form.value.resetDay;
    // if (month && day) {
    //   const year = new Date().getFullYear();
    //   this.nextResetDate = new Date(year, month - 1, day).toDateString();
    // } else {
    //   this.nextResetDate = null;
    // }
    // this.workflow.resetClientAppNumberDay = day;
    // this.workflow.resetClientAppNumberMonth = month;
    if (this.workflow && month && day) {
      this._workflowService
        .calculateClientAppNumberResetDate(month, day)
        .subscribe(resetDate => {
          this.nextResetDate = resetDate;
        });
    }
  }

  onAutoResetToggle(event: any) {
    this.isAutoResetEnabled = event?.target?.checked ?? false;
    if (this.isAutoResetEnabled) {
      this.form.get('resetMonth')?.setValidators(Validators.required);
      this.form.get('resetDay')?.setValidators(Validators.required);
    } else {
      this.form.get('resetMonth')?.clearValidators();
      this.form.get('resetDay')?.clearValidators();
      this.form.patchValue({
        resetMonth: null,
        resetDay: null
      });
    }
    this.form.get('resetMonth')?.updateValueAndValidity();
    this.form.get('resetDay')?.updateValueAndValidity();
  }

  toggleAutoReset() {
    if (!this.isAutoResetEnabled) {
      this.form.patchValue({
        resetMonth: null,
        resetDay: null
      });
      this.nextResetDate = null;
    }
  }

  // Visible to Public Methods
  onPublicToggle(event: any) {
    this.visibleToPublic = event?.target?.checked ?? event?.checked ?? false;
  }

  // Auto Void Methods
  toggleAutoVoid() {
    const autoVoidGroup = this.form.get('autoVoidSettings') as FormGroup;
    if (this.isAutoVoidEnabled) {
      autoVoidGroup.get('goBeyondActivity')?.setValidators(Validators.required);
      autoVoidGroup
        .get('daysUntilVoid')
        ?.setValidators([Validators.required, Validators.min(1)]);
      // create object agar nahi hai
      if (!this.workflow.autoVoidSettings) {
        this.workflow.autoVoidSettings = {
          goBeyondActivity: '',
          daysUntilVoid: 0
        };
      }
      // form enable
      autoVoidGroup.enable();
    } else {
      // OFF → clear
      // ❗ VERY IMPORTANT
      autoVoidGroup.get('goBeyondActivity')?.clearValidators();
      autoVoidGroup.get('daysUntilVoid')?.clearValidators();
      this.workflow.autoVoidSettings = null;
      autoVoidGroup.reset();
      autoVoidGroup.disable();
    }
    autoVoidGroup.get('goBeyondActivity')?.updateValueAndValidity();
    autoVoidGroup.get('daysUntilVoid')?.updateValueAndValidity();
    this.calculateAutoVoidExample();
  }

  onAutoVoidToggle(event: any) {
    this.isAutoVoidEnabled = event?.target?.checked ?? false;
    this.toggleAutoVoid(); // 👈 important
  }

  calculateAutoVoidExample() {
    setTimeout(() => {
      if (
        this.workflow.autoVoidSettings &&
        this.workflow.autoVoidSettings.goBeyondActivity &&
        this.workflow.autoVoidSettings.daysUntilVoid &&
        this.autoVoidActivities
      ) {
        const startDate = moment();
        const targetDate = moment();
        targetDate.add(this.workflow.autoVoidSettings.daysUntilVoid, 'days');
        const targetActivity = this.autoVoidActivities.find(
          a => a.id == this.workflow.autoVoidSettings.goBeyondActivity
        );

        if (targetActivity) {
          this.autoVoidExample = `An Application that starts on ${startDate.format(
            'MM/DD/YYYY'
          )} and doesn't complete the '${
            targetActivity.model.screenName
          }' Activity before the end of the day on ${targetDate.format(
            'MM/DD/YYYY'
          )} will get automatically voided the night of ${targetDate.format(
            'MM/DD/YYYY'
          )}.`;
        } else {
          this.autoVoidExample = null;
        }
      } else {
        this.autoVoidExample = null;
      }
    }, 100);
  }

  loadWorkflowActivities() {
    // if (!this.workflow) return;
    // const isNewWorkflow =
    //   !this.workflow.id || this.workflow.id === Utilities.EMPTY_GUID;
    // if (isNewWorkflow) {
    //   // 🔥 fallback static options
    //   this.activityOptions = [
    //     { label: 'Start', value: 'start' },
    //     { label: 'Review', value: 'review' },
    //     { label: 'Approval', value: 'approval' }
    //   ];
    //   return;
    // }
    // this._workflowService
    //   .getWorkflowActivities(this.workflow)
    //   .subscribe({
    //     next: (activities: any[]) => {
    //       this.autoVoidActivities = activities.map(a => ({
    //         label: a.name,
    //         value: a.id
    //       }));
    //     },
    //     error: (err) => {
    //       console.error('Failed to load activities:', err);
    //       this.activityOptions = [];
    //     }
    //   });
    this._workflowService
      .getWorkflowActivities(this.workflow)
      .subscribe(activities => {
        if (activities) {
          this.autoVoidActivities = activities
            .filter(a => a.showOnScreen)
            .sort((a, b) => (a.level < b.level ? -1 : 1));
          this.activityOptions = this.autoVoidActivities.map(a => ({
            label: a.model.screenName,
            value: a.id
          }));
        }
      });
  }

  // Administrative Role Methods
  selectRole(event: any) {
    const roleId = event?.value || event?.target?.value;
    if (!roleId) return;
    const selectedRole = this.adminRoles.find(
      r => r.id.toLowerCase() === roleId.toLowerCase()
    );
    if (selectedRole) {
      // 👉 workflow update
      this.workflow.version.graph.adminRole = selectedRole;
      this.workflow.version.adminRole = selectedRole;
      this.workflow.version.adminRoleId = selectedRole.id;
      // 👉 form update
      this.form.controls['adminRole'].setValue(selectedRole.id);
      this.adminRoleValid = true;
    } else {
      this.adminRoleValid = false;
    }
  }

  // Workflow Type Methods
  workflowTypeChanged(event: any) {
    const value = event?.value ?? event;
    this.workflow.workflowType = value;
    this.loadRenewalOptions();
    this.setExpirationValidator();
  }

  async loadRenewalOptions() {
    if (this.workflow.workflowType == WorkflowType.Renewable) {
      const workflows = await this._workflowService
        .getRenewableWorkflows(
          this._context.client,
          false,
          !this._context.client
        )
        .toPromise();

      const responseWorkflows: any[] = [];

      for (const wf of workflows) {
        if (wf.id === this.workflow.id) {
          responseWorkflows.push({
            label: 'Re-use this workflow',
            value: wf.id
          });
        } else {
          responseWorkflows.push({
            label: wf.version.name,
            value: wf.id
          });
        }
      }

      const thisWfIsPublished = responseWorkflows.some(
        w => w.value === this.workflow.id
      );

      // ✅ ALWAYS assign new array (NO push)
      if (this.workflow.id === Utilities.EMPTY_GUID || !thisWfIsPublished) {
        this.renewalWorkflowOptions = [
          {
            label: 'Re-use this workflow',
            value: this.workflow.id
          },
          ...responseWorkflows
        ];
      } else {
        this.renewalWorkflowOptions = responseWorkflows;
      }

      // 🔥 FINAL FIX (value ensure karo AFTER options)
      const exists = this.renewalWorkflowOptions.some(
        o => o.value === this.workflow.renewalWorkflowId
      );

      if (!exists) {
        this.workflow.renewalWorkflowId = this.workflow.id;
      }
      this.cdr.detectChanges(); // 🔥 FORCE UPDATE
    }
  }

  onExpirationOptionChange(event: any) {
    const option = event?.value ?? event;
    // this.selectedExpirationOption = {
    //   ...this.selectedExpirationOption,
    //   value: option
    // };
    this.selectedExpirationOption.value = option;

    this.form.patchValue({
      expirationType: option
    });
    this.setExpirationValidator();
  }

  loadExpirationOptions() {
    this.customFormulaCriteria = [];

    let selectedValue = 'infinite';

    if (this.workflow.expirationFormulaCriteria == null) {
      selectedValue = 'infinite';
    } else if (
      this.workflow.expirationFormulaCriteria.some(
        efc => efc.id === '196B539E-B330-4E4B-B577-A850D73B13AA'
      )
    ) {
      selectedValue = 'end-of-issue-year';
    } else if (
      this.workflow.expirationFormulaCriteria.some(
        efc => efc.id === '475A27B0-1AD9-4840-8E63-BB290D979F6C'
      )
    ) {
      selectedValue = 'custom-months';

      const tls = this.workflow.expirationFormulaCriteria[0]?.value
        ?.match(/\d/g)
        ?.join('');

      this.form.patchValue({
        termLength: parseInt(tls, 10)
      });
    } else {
      selectedValue = 'custom-formula';
      this.customFormulaCriteria = this.workflow.expirationFormulaCriteria;
    }

    // 🔥 BOTH SYNC
    this.selectedExpirationOption.value = selectedValue;

    this.form.patchValue({
      expirationType: selectedValue
    });
  }

  setExpirationValidator() {
    const type = this.form.value.workflowType;
    const option = this.selectedExpirationOption.value;
    // custom months
    if (type === 4 && option === 'custom-months') {
      this.form
        .get('termLength')
        ?.setValidators([Validators.required, Validators.min(1)]);
    } else {
      this.form.get('termLength')?.clearValidators();
    }
    // custom formula
    if (type === 4 && option === 'custom-formula') {
      this.customFormulaForm.get('formula')?.setValidators(Validators.required);
    } else {
      this.customFormulaForm.get('formula')?.clearValidators();
    }
    this.form.get('termLength').updateValueAndValidity();
    this.customFormulaForm.updateValueAndValidity();
  }

  // Requirements Methods

  buildEntities(): Observable<DataEntity[]> {
    return this._workflowService.getDataEntities(
      this.workflow || this._context.workflow,
      null,
      null,
      [WorkflowService.ACTIVITIES.Form]
    );
  }

  // Added by Amisha on 2026-06-03:
  // Loads the full workflow entity list so the Janus search dropdown matches the
  // older workflow settings behavior instead of showing only form-scoped entities.
  loadEntityOptions() {
    this._workflowService
      .getDataEntities(
        this.workflow || this._context.workflow,
        null,
        null,
        null
      )
      .subscribe({
        next: entities => {
          this.entities = entities;
          const entityOptionsByTemplateCode = new Map<
            string,
            { label: string; value: string }
          >();

          entities
            .filter(entity => !!entity?.templateCode)
            .forEach(entity => {
              const templateCode = entity.templateCode as string;

              if (!entityOptionsByTemplateCode.has(templateCode)) {
                entityOptionsByTemplateCode.set(templateCode, {
                  label: templateCode,
                  value: templateCode
                });
              }
            });

          this.entityOptions = Array.from(entityOptionsByTemplateCode.values());
          this.filterEntityOptions();
        },
        error: error => {
          console.error('Failed to load entities:', error);
          this.entities = [];
          this.entityOptions = [];
          this.filteredEntityOptions = [];
        }
      });
  }

  addRequirement() {
    this.newRequirement = new Requirement();
    this.newRequirement.id = Utilities.generateId();
  }

  newRequirementCancelled() {
    this.newRequirement = null;
  }

  newRequirementApplied(req: Requirement) {
    this.workflow.requirements.push(req);
    // this.requirements.push(req);
    this.newRequirement = null;
    this.saveWorkflow();
  }

  applyRequirement(req: any) {
  }

  editRequirement(req: any) {
    this.requirements.forEach(r => (r.isEditing = false));
    req.isEditing = true;
  }

  requirementUpdated(updated: any) {
    const index = this.requirements.findIndex(r => r.id === updated.id);
    if (index !== -1) {
      this.requirements[index] = updated;
    }
    this.requirements.forEach(r => (r.isEditing = false));
  }

  cancelEditRequirement(req: any) {
    req.isEditing = false;
  }

  removeRequirement(req: any) {
    const index = this.workflow.requirements.indexOf(req);
    this.workflow.requirements.splice(index, 1);
    this.saveWorkflow();
  }

  updateExpirationCriteria(criteria: ConditionTarget[]) {
    this.customFormulaCriteria = criteria;
    let hasValue = true;
    if (criteria && criteria.length > 0) {
      criteria.forEach((c, index) => {
        if ((c.value || '') === '') {
          hasValue = false;
          return false;
        }
      });
    }
    this.customFormulaForm.controls['formula'].setValue(hasValue ? 'done' : '');
  }

  // Lifecycle Methods

  initForm() {
    this.form = new FormGroup({
      name: new FormControl('', Validators.required), // REQUIRED
      description: new FormControl(''),
      departmentId: new FormControl(null, Validators.required), // REQUIRED
      adminRole: new FormControl(null, Validators.required), // REQUIRED
      workflowType: new FormControl(3, Validators.required), // REQUIRED
      // 🔥 Application number
      // numberPrefix: new FormControl('', Validators.required),
      startingNumber: new FormControl(null),
      totalLength: new FormControl(null),
      termLength: new FormControl(null),
      // 🔥 Auto reset
      resetMonth: new FormControl(null),
      resetDay: new FormControl(null),
      renewalWorkflowId: new FormControl(null),
      expirationType: new FormControl(null),
      // 🔥 Auto void
      // enableAutoVoid: new FormControl(null),
      autoVoidSettings: new FormGroup({
        goBeyondActivity: new FormControl(null),
        daysUntilVoid: new FormControl(null)
      })
    });
  }

  loadWorkflowForEdit() {
    this._workflowService.getWorkflow(this.workflowId).subscribe({
      next: workflow => {
        this.workflow = workflow;
        this._context.workflow$.emit(this.workflow);
        const departmentid =
          (workflow as any)?.departmentId ||
          this._context.staffDepartmentId ||
          '';
        const requirements = (workflow as any)?.requirements || '';
        // 🔥 FORM PATCH (IMPORTANT)
        this.form.patchValue({
          name: workflow.version?.name,
          description: workflow.version?.description,
          departmentId: departmentid,
          startingNumber: workflow.clientApplicationNumber,
          totalLength: workflow.fixedClientApplicationNumberLength,
          adminRole: workflow.version?.adminRoleId,
          workflowType: workflow.workflowType,
          resetMonth: workflow.resetClientAppNumberMonth,
          resetDay: workflow.resetClientAppNumberDay,
          renewalWorkflowId: this.workflow.renewalWorkflowId || this.workflow.id
        });

        // 🔥 EXTRA FIELDS
        this.visibleToPublic = workflow.visibleToPublic;
        this.isAutoResetEnabled = !!workflow.resetClientAppNumberMonth;
        this.isAutoVoidEnabled = !!workflow.autoVoidSettings;

        if (workflow.autoVoidSettings) {
          this.form.get('autoVoidSettings')?.patchValue({
            goBeyondActivity: workflow.autoVoidSettings.goBeyondActivity,
            daysUntilVoid: workflow.autoVoidSettings.daysUntilVoid
          });
        }

        // 🔥 categories
        this.isCategoryEnabled = (workflow.categories?.length ?? 0) > 0;
        this.categories = (workflow.categories || []).sort(
          (a, b) => (a?.ordinal || 0) - (b?.ordinal || 0)
        );

        // 🔥 tags
        this.selectedTags = workflow.workflowTags?.map(t => t.tag) || [];

        // 🔥 preview
        this.calculateFixedNumberPreview();

        // 🔥 activities load
        this.loadWorkflowActivities();

        // 🔥 requirements
        this.requirements = requirements;
        this.loadRenewalOptions();
        this.loadExpirationOptions();
        this.calculateAutoVoidExample();
        this.calcNextDate = true;
        const autoVoidGroup = this.form.get('autoVoidSettings');

        if (autoVoidGroup) {
          autoVoidGroup.valueChanges.subscribe(() => {
            this.calculateAutoVoidExample();
          });
        }

        this.loadEntityOptions();

        this.calculateNextResetDate();
      },
      error: () => {
        this._toastr.error('Failed to load workflow');
      }
    });
  }

  createNewWorkflow() {
    this.workflow = new Workflow({
      id: Utilities.EMPTY_GUID,
      client: this.clientMode ? this._context.client : undefined,
      isTemplateLibraryWorkflow: false,
      workflowType: WorkflowType.Other,
      workflowTags: [],
      // ADD THIS
      clientApplicationNumber: 0,
      fixedClientApplicationNumberLength: 0,
      version: new WorkflowVersion({
        graph: new WorkflowGraph({
          onStarted: new OnStartedActivity({
            activities: [
              new StartWorkflowActivity({
                id: Utilities.generateId(),
                model: new StartWorkflowActivityModel({
                  responsibleRole: SecurityService.APPLICANT_ROLE,
                  responsibleRoleId: SecurityService.APPLICANT_ROLE.id
                })
              })
            ]
          })
        })
      })
    });

    this.loadEntityOptions();

    this.workflowCategories = [];

    this.isAutoVoidEnabled = !!this.workflow.autoVoidSettings;
    this.form.patchValue({
      departmentId: this._context.staffDepartmentId || null,
      autoVoidSettings: {
        goBeyondActivity:
          this.workflow?.autoVoidSettings?.goBeyondActivity ?? null,
        daysUntilVoid: this.workflow?.autoVoidSettings?.daysUntilVoid ?? null
      }
    });
    if (this.isAutoVoidEnabled) {
      this.toggleAutoVoid();
    }

    // this.loadWorkflowActivities();
    this.loadRenewalOptions();
    //     this.form.patchValue({
    //   startingNumber: 0,
    //   totalLength: 0
    // });
    this.calculateFixedNumberPreview();
  }

  get isEditMode(): boolean {
    return !!this.workflowId && this.workflowId !== Utilities.EMPTY_GUID;
  }
  ngOnInit() {
    this.isEditorVisible = true;
    // this.selectedDepartmentId = (this.workflow as any)?.departmentId || '';
    // this.selectedDepartmentId = '';
    this.workflowId = this.route.snapshot.params['id'];
    // STEP 1: form initialize
    this.initForm();

    const autoVoidGroup = this.form.get('autoVoidSettings');

    if (autoVoidGroup) {
      autoVoidGroup.valueChanges.subscribe(val => {
        this.workflow.autoVoidSettings = val;
        this.calculateAutoVoidExample();
      });
    }

    // STEP 2: subscriptions

    //Start administrative role
    //End administrative role

    this.months = range(1, 13, 1).map((m, idx) => {
      return {
        label: moment()
          .month(m - 1)
          .format('MMMM'),
        value: m
      };
    });
    this.days = range(1, 29).map(d => ({
      label: d.toString(),
      value: d
    }));
    this.categories = [];
    if (this.isCategoryEnabled) {
      this.ensureStaticCategories();
    }
    this.selectedExpirationOption = {
      value: '',
      label: '',
      expSettings: []
    };
    // this.selectedExpirationOption = null;
    this._clientService
      .getClientDepartments(this._context.client.id, true)
      .subscribe({
        next: departments => {
          this.departments = departments;
          // 👇 IMPORTANT mapping
          this.departmentOptions = departments.map(d => ({
            label: d.name,
            value: d.id
          }));
        },
        error: error => {
          console.error('Failed to load departments:', error);
          this.departments = [];
        }
      });

    if (this.workflowId && this.workflowId !== Utilities.EMPTY_GUID) {
      this.loadWorkflowForEdit();
    } else {
      this.createNewWorkflow();
    }

    // this.loadWorkflowActivities();
    // Initialize workflow categories for new workflow

    // Tags dropdown options Api call and mapping
    this._systemService
      .getTagsAlphabetically(this._context.client.id)
      .subscribe(tags => {
        this.allTags = tags;
        this.tagOptions = tags.map(t => ({
          label: t.name,
          value: t.id
        }));
      });
    // End Tags dropdown options Api call and mapping

    // Start Administrative Role
    this._securityService
      .getRolesWithAllActions(this._context.client.id, Actions.WORKFLOW_ACTIONS)
      .subscribe({
        next: roles => {
          this.adminRoles = roles;
          // dropdown mapping
          this.roleOptions = roles.map(r => ({
            label: r.name,
            value: r.id
          }));
        },
        error: err => {
          console.error('Failed to load roles:', err);
        }
      });
    // End Administrative Role

    if (this.form.controls['totalLength']) {
      this.form.controls['totalLength'].valueChanges.subscribe(() => {
        this.calculateFixedNumberPreview();
      });
    }
    if (this.form.controls['startingNumber']) {
      this.form.controls['startingNumber'].valueChanges.subscribe(() => {
        this.calculateFixedNumberPreview();
      });
    }
  }

  // General Methods
  close() {
    this.isEditorVisible = false;
    this.workflowJanusService.setNewWorkflowButtonDisabled(false);
    this.router.navigate([{ outlets: { editWorkflow: null } }], {
      relativeTo: this.route.parent
    });
  }

  getError(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.touched) return '';
    if (control.errors?.['required']) {
      return 'This field is required';
    }
    if (control.errors?.['min']) {
      return 'Value must be greater than 0';
    }
    if (control.errors?.['maxlength']) {
      return 'Too long value';
    }
    return '';
  }

  getAutoVoidError(controlName: string): string {
    const control = this.form.get('autoVoidSettings.' + controlName);
    if (!control || !control.touched) return '';
    if (control.errors?.['required']) {
      return 'This field is required';
    }
    return '';
  }

  private async saveCategoriesForNewWorkflow(
    workflowId: string
  ): Promise<void> {
    const categoriesToSave = this.categories.map(cat => ({
      ...cat,
      workflowId: workflowId
    }));
    const savePromises = categoriesToSave.map(category =>
      this._workflowService.saveWorkflowCategory(category).toPromise()
    );
    await Promise.all(savePromises);
  }

  saveWorkflow() {
    //  STEP 1: mark touched
    this.form.markAllAsTouched();
    //  STEP 2: global validation
    if (this.form.invalid) {
      this._toastr.error('Please fill all required fields');
      return;
    }
    //  STEP 3: workflow reference
    const wf: any = this.workflow;
    const formValue = this.form.value;
    // -------------------------------
    // BASIC FIELDS
    // -------------------------------
    wf.version.name = formValue.name;
    wf.version.description = formValue.description;
    wf.departmentId = this.form.value.departmentId;
    wf.visibleToPublic = this.visibleToPublic;
    // -------------------------------
    // APPLICATION NUMBER SETTINGS
    // -------------------------------
    wf.clientApplicationNumber = formValue.startingNumber;
    wf.fixedClientApplicationNumberLength = formValue.totalLength;
    wf.numberPrefixCriteria = this.workflow.numberPrefixCriteria;
    // -------------------------------
    // AUTO RESET (LIKE ORIGINAL)
    // -------------------------------
    if (this.isAutoResetEnabled) {
      wf.resetClientAppNumberMonth = formValue.resetMonth;
      wf.resetClientAppNumberDay = formValue.resetDay;
    } else {
      wf.resetClientAppNumberMonth = null;
      wf.resetClientAppNumberDay = null;
    }
    // -------------------------------
    // AUTO VOID (LIKE ORIGINAL)
    // -------------------------------
    if (this.isAutoVoidEnabled) {
      wf.autoVoidSettings = {
        goBeyondActivity: formValue.autoVoidSettings?.goBeyondActivity,
        daysUntilVoid: formValue.autoVoidSettings?.daysUntilVoid
      };
    } else {
      wf.autoVoidSettings = null;
    }
    // -------------------------------
    // ADMIN ROLE (IMPORTANT)
    // -------------------------------
    const roleId = formValue.adminRole;
    const selectedRole = this.adminRoles.find(r => r.id === roleId);
    if (selectedRole) {
      wf.version.adminRole = selectedRole;
      wf.version.adminRoleId = selectedRole.id;
      wf.version.graph.adminRole = selectedRole;
    }
    // -------------------------------
    // WORKFLOW TYPE
    // -------------------------------
    wf.workflowType = formValue.workflowType;
    const autoVoid = this.form.value.autoVoidSettings;
    wf.autoVoidSettings = this.isAutoVoidEnabled
      ? {
          goBeyondActivity: autoVoid?.goBeyondActivity,
          daysUntilVoid: autoVoid?.daysUntilVoid
        }
      : null;
    const option = this.form.value.expirationType;
    // switch (this.selectedExpirationOption.value) {
    switch (option) {
      case 'infinite':
        this.selectedExpirationOption.expSettings = null;
        break;
      case 'end-of-issue-year':
        this.selectedExpirationOption.expSettings = [
          new ConditionTarget({
            id: '196B539E-B330-4E4B-B577-A850D73B13AA', // hard-coded for ease of selected option loading
            value: 'DATE(YEAR(${EFFECTIVE_DATE}),12,31)'
          })
        ];
        break;
      case 'custom-months':
        this.selectedExpirationOption.expSettings = [
          new ConditionTarget({
            id: '475A27B0-1AD9-4840-8E63-BB290D979F6C', // hard-coded for ease of selected option loading
            value: `EDATE(\${EFFECTIVE_DATE}, ${this.form.value.termLength})`
          })
        ];
        break;
      case 'custom-formula':
        this.selectedExpirationOption.expSettings = this.customFormulaCriteria;
        break;
      default:
        this.selectedExpirationOption.expSettings = null;
        break;
    }
    this.workflow.expirationFormulaCriteria = this.selectedExpirationOption.expSettings;
    // -------------------------------
    // TAGS (ALREADY HANDLED)
    // -------------------------------
    wf.workflowTags = this.workflow.workflowTags;
    // -------------------------------
    // CATEGORIES (IMPORTANT)
    // -------------------------------
    wf.categories = this.isCategoryEnabled ? this.categories : [];
    const isNew =
      !this.workflow.id || this.workflow.id === Utilities.EMPTY_GUID;
    this.isSaving = true;
    // -------------------------------
    // STEP 4: SAVE API
    // -------------------------------

    // prepare the workflow activities for saving
    this.workflow.version.graph.onStarted.activities.forEach(activity => {
      activity.prepareForSave();
    });

    this._workflowService.saveWorkflow(this.workflow).subscribe({
      next: wf => {
        this.isSaving = false;
        // 👉 update local workflow
        this.workflow = wf;
        this.workflowId = wf.id;
        // 🔥 NEW WORKFLOW CASE
        if (isNew) {
          if (this.categories?.length > 0) {
            this.saveCategoriesForNewWorkflow(wf.id)
              .then(() => {
                // this._toastr.success('Workflow & categories saved!');
                this.router.navigate(['admin', 'workflow-builder', wf.id],{
        state: {
          returnUrl: this.router.url
        },
      });
              })
              .catch(() => {
                this._toastr.error('Workflow saved but category failed');
                this.router.navigate(['admin', 'workflow-builder', wf.id],{
        state: {
          returnUrl: this.router.url
        },
      });
              });
          } else {
            this.router.navigate(['admin', 'workflow-builder', wf.id],{
        state: {
          returnUrl: this.router.url
        },
      });
          }
        } else {
          // 🔥 EXISTING WORKFLOW CASE
          this.workflow = wf;
          // Initialize workflow categories from saved workflow
          this.workflowCategories = this.workflow.categories || [];
          this._toastr.success('Saved!');
          this.loadRenewalOptions();
          this.isEditorVisible = false;
          this.router.navigate([{ outlets: { editWorkflow: null } }], {
            relativeTo: this.route.parent
          });
        }
      },
      error: () => {
        this._toastr.error('Failed to save workflow');
      }
    });
  }
}
